import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { playSoundLogo } from "./sound";

const SOCIAL_URLS = {
  facebook: "https://www.facebook.com/profile.php?id=61579587810996",
  linkedin: "https://www.linkedin.com/in/chris-dickson-138225380/",
  tiktok: "https://www.tiktok.com/@launchpadtv5",
  x: "https://x.com/launchpad_tv3",
  youtube: "https://www.youtube.com/@Launchpad_TV",
  reddit: "https://www.reddit.com/user/LAUNCHPAD-TV",
  twitch: "https://www.twitch.tv/launchyou",
  rumble: "https://rumble.com/user/LaunchpadTV",
};

const INTRO_TTL_MS = 30 * 24 * 60 * 60 * 1000;

const seenRecently = () => {
  const at = Number(localStorage.getItem("lp_intro_seen_at"));
  return at > 0 && Date.now() - at < INTRO_TTL_MS;
};

export default function Intro({ onDone }) {
  const [skip] = useState(
    () =>
      (seenRecently() || !!window.location.hash) &&
      !new URLSearchParams(window.location.search).has("intro")
  );
  const [phase, setPhase] = useState(0);
  const [open, setOpen] = useState(!skip);

  useEffect(() => {
    if (skip) {
      onDone?.();
      return;
    }
    document.body.style.overflow = "hidden";
    window.__lenis?.stop();
    playSoundLogo(0.75);
    const timers = [
      setTimeout(() => setPhase(3), 7400),
      setTimeout(() => finish(), 10000),
    ];
    return () => timers.forEach(clearTimeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleEnded = () => {
    setPhase(3);
    setTimeout(finish, 2600);
  };

  const finish = () => {
    localStorage.setItem("lp_intro_seen_at", String(Date.now()));
    document.body.style.overflow = "";
    window.__lenis?.start();
    setOpen(false);
    onDone?.();
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="intro-overlay"
          className="fixed inset-0 z-[100] bg-obsidian flex items-center justify-center overflow-hidden"
          exit={{ opacity: 0, scale: 1.15, transition: { duration: 0.9, ease: [0.16, 1, 0.3, 1] } }}
        >
          <div className="absolute inset-0 scanlines" />
          <div className="absolute inset-0 noise-overlay" />

          <video
            className="absolute inset-0 w-full h-full object-cover md:object-contain"
            src="/intro-concept.mp4"
            poster="/intro-poster.jpg"
            autoPlay
            muted
            playsInline
            onEnded={handleEnded}
            data-testid="intro-video"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-obsidian/80 via-transparent to-obsidian/40 pointer-events-none" />

          <div className="absolute top-8 left-1/2 -translate-x-1/2 font-mono text-[10px] tracking-[0.35em] uppercase text-white/80 flex items-center gap-3 bg-obsidian/50 backdrop-blur-sm px-4 py-2">
            <span className="h-1.5 w-1.5 rounded-full bg-signal animate-live-dot" />
            Launchpad TV — Signal Acquisition
          </div>

          {phase >= 3 && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5 }}
              className="absolute inset-0 bg-obsidian flex flex-col items-center justify-center px-6"
              data-testid="intro-badge-beat"
            >
              <motion.img
                src="/logo-full.webp"
                width={800}
                height={801}
                alt="Launchpad TV — Launch Where You Land"
                className="w-56 sm:w-72 h-auto drop-shadow-[0_0_60px_rgba(63,169,245,0.5)]"
                initial={{ opacity: 0, scale: 0.75, filter: "blur(14px)" }}
                animate={{ opacity: 1, scale: 1, filter: "blur(0px)" }}
                transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
              />
              <div className="mt-8 text-center overflow-hidden">
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.5 }}
                  className="font-mono text-[10px] tracking-[0.35em] uppercase text-slate-400"
                >
                  Global signal live — 11.14.2026
                </motion.div>
              </div>
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.1, duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
                className="mt-7 flex flex-col items-center gap-3"
                data-testid="intro-social-strip"
              >
                <span className="font-mono text-[9px] tracking-[0.3em] uppercase text-slate-500">
                  Follow the Signal
                </span>
                <div className="flex items-center gap-3.5">
                  {Object.entries(SOCIAL_URLS).map(([id, url]) => (
                    <a
                      key={id}
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={`Launchpad TV on ${id}`}
                      data-testid={`intro-social-${id}`}
                      className="opacity-50 hover:opacity-100 transition-opacity duration-300"
                    >
                      <img src={`/social/${id}.svg`} alt="" className="w-4 h-4" />
                    </a>
                  ))}
                </div>
              </motion.div>
            </motion.div>
          )}

          <button
            data-testid="intro-skip-button"
            onClick={finish}
            className="absolute bottom-[calc(1.5rem+env(safe-area-inset-bottom))] right-5 sm:bottom-8 sm:right-8 font-mono text-[10px] tracking-[0.3em] uppercase text-slate-400 hover:text-signal border border-white/10 hover:border-signal/60 px-5 py-3.5 transition-colors duration-300 bg-obsidian/40"
          >
            Skip // Enter Site
          </button>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
